//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { getSamplingCatalogProducts } from '@apis/campaign-requests/sampling/products/samplingProductsApiV2';
import {
  SamplingCatalogProductsRequest,
  SamplingCatalogProductsResponse,
} from '@apis/campaign-requests/sampling/products/samplingProductsApiV2Model';
import { deserializeRightsPermissions } from '@apis/rights-management/rightsManagementApiUtils';
import {
  BatchGetAllVariantsRequest,
  BatchGetAllVariantsResponse,
  CuralateCatalogProduct,
  CuralateCatalogProducts,
  CuralateCatalogProductsRequest,
  CuralateProductCatalog,
  CuralateProductImage,
  IGetPublishersForCreatorRequest,
  IGetTaggedProductsStatsRequest,
  IIntelligentProductTaggingFeedbackUserRequest,
  IMediaItemTagsRequest,
  IProductCatalogClientsRequest,
  IProductCatalogClientsResponse,
  SetCustomThumbnailRequestBody,
  SetCustomThumbnailResponse,
  TrackingParamsResponse,
  TrackingUrlPreviewRequest,
  UploadCoverImageResult,
} from '@apis/universal-image-actions/universalImageActionsApiModel';
import {
  deserializeCatalogs,
  deserializeItemMetadataTags,
} from '@apis/universal-image-actions/universalImageActionsApiUtils';
import { ICrawlProductsRequest } from '@legacy-components/common/universal-image-actions/universal-image-actions-api-service';
import Cuid from '@models/Cuid';
import FileId from '@models/FileId';
import IItemMetadata, {
  Product,
  UiaProductTagRequest,
} from '@models/IItemMetadata';
import PartialCuid from '@models/PartialCuid';
import { IGetPublishersForCreatorResponse } from '@share/api/shareApi';
import { IShareTaggedProductsStats } from '@share/models/shareStateModel';
import { makeParams } from '@utils/makeParams';
import {
  serializedGatewayFetch,
  serializedSessionFetch,
} from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

interface IBeautifyImageRequest {
  imageUrl: string;
  fileId?: FileId;
}

const gatewaySyndicationFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedSessionFetch(`/api/syndication${endpoint}`, init);
};

const universalImageActionsFetch = (
  endpoint: string,
  init?: RequestInit,
  useFetchHeader?: boolean,
): Promise<ISerializedResponse> => {
  return serializedSessionFetch(
    `/api/universal-image-actions${endpoint}`,
    init,
    useFetchHeader,
  );
};

const universalImageActionsFetchV2 = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'web_gateway',
    `/api/v2/universal-image-actions${endpoint}`,
    init,
  );
};

const webGatewayProductStylesFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'web_gateway',
    `/api/v1/products${endpoint}`,
    init,
  );
};

const universalImageActionsCustomCoverFetch = (
  endpoint: string,
  init?: RequestInit,
  useFetchHeader?: boolean,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'web_gateway',
    `/api/v1/videos${endpoint}`,
    init,
    useFetchHeader,
  );
};

const syndicationGatewayFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch('syndication_gateway', endpoint, init);
};

const settingsFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedSessionFetch(`/api/settings${endpoint}`, init);
};

const webGatewayFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch('sampling_gateway', endpoint, init);
};

/**
 * Intelligent Product Tagging Gateway service
 */
const iptGatewayFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch('ipt_gateway', endpoint, init);
};

/**
 * Beautifies an image given an itemId and new image url
 *
 * /api/universal-image-actions/items/:itemId/beautify
 */
export const beautifyImage = async (
  itemId: PartialCuid,
  req: IBeautifyImageRequest,
): Promise<void> => {
  await universalImageActionsFetch(`/items/${itemId}/beautify`, {
    method: 'PUT',
    body: JSON.stringify(req),
  });
};

/**
 * Gets metadata for the currently selected cuid in UIA
 * /api/v2/universal-image-actions/items/:cuid/metadata
 *
 * @param cuid: The Cuid to get metadata for
 *
 * @return Promise<IItemMetadata>: an item metadata object
 */
export const getMediaItemMetadata = async (
  cuid: Cuid,
  init?: RequestInit,
): Promise<IItemMetadata> => {
  const res = await universalImageActionsFetchV2(`/items/${cuid}/metadata`, {
    ...(init ?? {}),
    method: 'GET',
  });

  return res.json<IItemMetadata>((res) => {
    return {
      ...res,
      productTags: deserializeItemMetadataTags(res.productTags),
      rightsPermissions: deserializeRightsPermissions(res.rightsPermissions),
    };
  });
};

/**
 * Update media item tags for the currently selected cuid in UIA
 * /api/universal-image-actions/items/:cuid/tags
 *
 * @param cuid: The Cuid to get tags for
 */
export const updateMediaItemTags = async (
  cuid: Cuid,
  request: IMediaItemTagsRequest,
  init?: RequestInit,
): Promise<void> => {
  const res = await universalImageActionsFetch(`/items/${cuid}/tags`, {
    ...(init ?? {}),
    body: JSON.stringify(request),
    method: 'PUT',
  });
  return res.json();
};

/**
 * TODO: Port this endpoint out of LWS
 * Gets the current configured tracking param for the client
 */
export const getCurrentTrackingParams = async (
  init?: RequestInit,
): Promise<TrackingParamsResponse> => {
  const res = await universalImageActionsFetch(
    `/products/tracking-params-current`,
    init,
  );
  return res.json<TrackingParamsResponse>();
};

/**
 * Gets tracking settings by version ID.
 * GET /api/universal-image-actions/products/tracking-params/:versionId
 *
 * @param versionId The version ID for the tracking settings.
 * @param init Optional request init options.
 * @returns A promise that resolves to the tracking parameters.
 */
export const getTrackingSettingsByVersion = async (
  { versionId }: { versionId: number },
  init?: RequestInit,
): Promise<TrackingParamsResponse> => {
  const res = await universalImageActionsFetch(
    `/products/tracking-params/${versionId}`,
    init,
  );
  return res.json<TrackingParamsResponse>();
};

/**
 * Searches for BV Catalog products
 *
 * @param params SamplingCatalogProductsRequest object containing search parameters
 * @returns Promise<SamplingCatalogProduct[]> Array of products
 */
export const searchBvCatalogProducts = async (
  params: SamplingCatalogProductsRequest,
  init?: RequestInit,
): Promise<SamplingCatalogProductsResponse> => {
  return getSamplingCatalogProducts(params, init);
};

/**
 * Searches for Curalate Catalog products
 *
 * @param requestParams CuralateCatalogProductsRequest object containing search parameters
 * @returns Promise<CuralateCatalogProduct[]> Array of product image digests
 */
export const searchCuralateCatalogProducts = async (
  requestParams: CuralateCatalogProductsRequest,
  init?: RequestInit,
): Promise<CuralateCatalogProducts> => {
  const queryParams = makeParams(requestParams);

  const response = await universalImageActionsFetchV2(
    `/products?${queryParams}`,
    {
      ...(init ?? {}),
      method: 'GET',
    },
  );

  const res = await response.json<{ data: CuralateCatalogProducts }>();

  return res.data;
};

/**
 * Get all connected product catalogs
 *
 * @returns Promise<CuralateProductCatalog[]> Array of curalate product catalogs
 */
export const getCuralateProductCatalogs = async (
  init?: RequestInit,
): Promise<CuralateProductCatalog[]> => {
  const response = await settingsFetch('/product-catalogs', {
    ...(init ?? {}),
    method: 'GET',
  });

  return response.json<CuralateProductCatalog[]>(deserializeCatalogs);
};

/**
 * Updates the PDP URL for a BV Catalog product
 */
export const updateBvProductPdpUrl = async (
  externalId: string,
  pdpUrl: string,
): Promise<{ correlationId: string }> => {
  const endpoint = `/api/v1/clients/products/${externalId}`;

  const response = await webGatewayFetch(endpoint, {
    method: 'PATCH',
    body: JSON.stringify({ pdpUrl }),
  });

  return response.json();
};

/**
 * Get the list of clients with available catalogs for product tagging an item.
 * This is only used when product tagging Share'd images.
 *
 * GET /syndication/images/${imageId}/product-catalog-clients
 */
export const getProductCatalogClients = async (
  { cuid }: IProductCatalogClientsRequest,
  init?: RequestInit,
): Promise<IProductCatalogClientsResponse> => {
  const response = await gatewaySyndicationFetch(
    `/images/${cuid}/product-catalog-clients`,
    {
      ...(init ?? {}),
      method: 'GET',
    },
  );

  return response.json<IProductCatalogClientsResponse>();
};

/**
 * Store new user feedback
 * POST /api/v1/ipt/user-feedback
 */
export const saveIntelligentProductTaggingUserFeedback = async (
  req: IIntelligentProductTaggingFeedbackUserRequest,
  init?: RequestInit,
): Promise<void> => {
  const endpoint = '/api/v1/ipt/user-feedback';

  const response = await iptGatewayFetch(endpoint, {
    ...(init ?? {}),
    method: 'POST',
    body: JSON.stringify(req),
  });

  return response.json();
};

/**
 * Get publishers for the creator
 * GET /api/v1/connect/creators/${creatorClientId}/publishers
 */
export const getPublishersForCreator = async (
  req: IGetPublishersForCreatorRequest,
  init?: RequestInit,
): Promise<IGetPublishersForCreatorResponse> => {
  const endpoint = `/api/v1/connect/creators/${req.clientId}/publishers`;

  const response = await syndicationGatewayFetch(endpoint, {
    ...(init ?? {}),
    method: 'GET',
  });

  return response.json<IGetPublishersForCreatorResponse>();
};

/**
 * Get tagged products stats
 * GET /api/v1/connect/tagged-products-stats/${publisherClientId}/${clientId}
 */
export const getTaggedProductsStats = async (
  req: IGetTaggedProductsStatsRequest,
  init?: RequestInit,
): Promise<IShareTaggedProductsStats> => {
  const endpoint = `/api/v1/connect/tagged-products-stats/${req.publisherClientId}/${req.clientId}`;

  const response = await syndicationGatewayFetch(endpoint, {
    ...(init ?? {}),
    method: 'GET',
  });

  return response.json<IShareTaggedProductsStats>();
};

/**
 * Update tagged products for the currently selected cuid in UIA
 * PUT /api/universal-image-actions/items/:cuid/products
 */
export const saveProductTags = async (
  { cuid, products }: { products: UiaProductTagRequest[]; cuid: PartialCuid },
  init?: RequestInit,
): Promise<{ products: Product[] }> => {
  const params = {
    products,
  };

  const response = await universalImageActionsFetch(`/items/${cuid}/products`, {
    ...init,
    method: 'PUT',
    body: JSON.stringify(params),
  });

  return response.json<{ products: Product[] }>((res) => {
    return {
      products: deserializeItemMetadataTags(res.products),
    };
  });
};

/**
 * Crawl products for the currently selected cuid in UIA
 * GET /api/universal-image-actions/products/crawl
 */
export const crawlProducts = async (
  req: ICrawlProductsRequest,
  init?: RequestInit,
): Promise<CuralateCatalogProduct[]> => {
  const params = makeParams(req);

  const response = await universalImageActionsFetch(
    `/products/crawl?${params}`,
    {
      ...init,
      method: 'GET',
    },
  );

  return response.json<CuralateCatalogProduct[]>();
};

/**
 * Upload an image to use as the product image
 * POST /api/universal-image-actions/products/upload-image
 */
export const uploadCatalogProductImage = async (
  file: File,
  init?: RequestInit,
): Promise<CuralateProductImage> => {
  const formData = new FormData();
  formData.append('file', file, file.name);

  const response = await universalImageActionsFetch(
    `/products/upload-image`,
    {
      ...init,
      method: 'POST',
      body: formData,
    },
    true,
  );

  return response.json<CuralateProductImage>();
};

/**
 * Gets full url with tracking parameters preview
 * POST /api/universal-image-actions/products/tracking-url-preview
 */
export const getTrackingUrlPreview = async (
  req: TrackingUrlPreviewRequest,
  init?: RequestInit,
): Promise<string> => {
  const res = await universalImageActionsFetch(
    '/products/tracking-url-preview',
    {
      ...init,
      method: 'POST',
      body: JSON.stringify(req),
    },
  );
  return res.text();
};

/**
 * Uploads a cover image for a post
 *
 * POST /api/v1/videos/cover-image
 */
export const uploadCoverImage = async (
  file: File,
  init?: RequestInit,
): Promise<UploadCoverImageResult> => {
  const formData = new FormData();
  formData.append('coverImage', file, file.name);

  const res = await universalImageActionsCustomCoverFetch(
    '/cover-image',
    {
      ...init,
      method: 'POST',
      body: formData,
    },
    true,
  );

  return (await res.json<{ data: UploadCoverImageResult }>()).data;
};

/**
 * Sets a custom thumbnail for a video
 *
 * POST /api/v1/videos/items/:itemId/experience/:experienceName/customThumbnail
 *
 * @param itemId The ID of the video item
 * @param requestBody The request body containing fileId or thumbnailOffsetMilliseconds
 * @param experienceName The experience name (defaults to 'gallery')
 * @param init Optional request init options
 * @returns Promise with the response data
 */
export const setCustomThumbnail = async (
  itemId: PartialCuid,
  requestBody: SetCustomThumbnailRequestBody,
  experienceName: string = 'gallery',
  init?: RequestInit,
): Promise<SetCustomThumbnailResponse> => {
  const res = await universalImageActionsCustomCoverFetch(
    `/item/${itemId}/experience/${experienceName}/customThumbnail`,
    {
      ...init,
      method: 'POST',
      body: JSON.stringify(requestBody),
    },
  );

  return (await res.json<{ data: SetCustomThumbnailResponse }>()).data;
};

/**
 * Get product variants for multiple product style IDs
 * POST /api/v1/products/batch
 */
export const batchGetAllVariants = async (
  req: BatchGetAllVariantsRequest,
  init?: RequestInit,
): Promise<BatchGetAllVariantsResponse> => {
  const res = await webGatewayProductStylesFetch('/batch', {
    ...init,
    body: JSON.stringify(req),
    method: 'POST',
  });

  const json = await res.json<{ data: BatchGetAllVariantsResponse }>();
  return json.data;
};
